// Analytics Service - Track user behavior and conversion metrics

class AnalyticsService {
  constructor() {
    this.isEnabled = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';
    this.apiEndpoint = process.env.REACT_APP_ANALYTICS_ENDPOINT || '/api/analytics';
    this.sessionId = this.generateSessionId();
    this.userId = null;
    this.events = [];
    this.startTime = Date.now();
    
    // Initialize if enabled
    if (this.isEnabled) {
      this.init();
    }
  }

  // Initialize analytics
  init() {
    try {
      // Track page load
      this.track('Page Loaded', {
        url: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // Set up event listeners
      this.setupEventListeners();
      
      // Batch send events every 2 minutes (reduced frequency)
      setInterval(() => {
        this.flushEvents();
      }, 120000);
      
      // Send events before page unload
      window.addEventListener('beforeunload', () => {
        this.flushEvents(true);
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Analytics initialized');
      }
    } catch (error) {
      console.error('Analytics initialization failed:', error);
    }
  }

  // Set user ID for tracking
  setUserId(userId) {
    this.userId = userId;
    if (process.env.NODE_ENV === 'development') {
      console.log('👤 Analytics user ID set:', userId);
    }
    this.track('User Identified', { userId });
    
    // Store in localStorage for persistence
    if (userId) {
      localStorage.setItem('analytics_user_id', userId);
    } else {
      localStorage.removeItem('analytics_user_id');
    }
  }

  // Get current user ID (with fallback to localStorage)
  getUserId() {
    return this.userId || localStorage.getItem('analytics_user_id');
  }

  // Track events
  track(eventName, properties = {}) {
    if (!this.isEnabled) return;
    
    try {
      const event = {
        event: eventName,
        properties: {
          ...properties,
          sessionId: this.sessionId,
          userId: this.userId,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          sessionDuration: Date.now() - this.startTime
        }
      };
      
      this.events.push(event);
      
      // Console log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Analytics Event:', event);
      }
      
      // Auto-flush for critical events
      if (this.isCriticalEvent(eventName)) {
        this.flushEvents();
      }
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  }

  // Track page views
  trackPageView(pageName, properties = {}) {
    this.track('Page View', {
      page: pageName,
      ...properties
    });
  }

  // Track conversions
  trackConversion(conversionType, properties = {}) {
    this.track('Conversion', {
      type: conversionType,
      ...properties
    });
  }

  // Track quote interactions
  trackQuoteEvent(action, quoteId, properties = {}) {
    this.track(`Quote ${action}`, {
      quoteId,
      ...properties
    });
  }

  // Track user journey stages
  trackFunnelStep(step, properties = {}) {
    this.track('Funnel Step', {
      step,
      ...properties
    });
  }

  // Track business metrics
  trackBusinessMetric(metric, value, properties = {}) {
    this.track('Business Metric', {
      metric,
      value,
      ...properties
    });
  }

  // Track errors
  trackError(error, context = {}) {
    this.track('Error', {
      message: error.message,
      stack: error.stack,
      context,
      severity: 'error'
    });
  }

  // Track performance metrics
  trackPerformance(metric, value, properties = {}) {
    this.track('Performance', {
      metric,
      value,
      ...properties
    });
  }

  // Set up automatic event listeners
  setupEventListeners() {
    // Track clicks on important elements
    document.addEventListener('click', (e) => {
      const element = e.target.closest('[data-analytics]');
      if (element) {
        const action = element.getAttribute('data-analytics');
        this.track('Element Clicked', {
          action,
          element: element.tagName,
          text: element.textContent?.substring(0, 100),
          href: element.href
        });
      }
    });

    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.tagName === 'FORM') {
        this.track('Form Submitted', {
          formId: form.id,
          formAction: form.action,
          method: form.method
        });
      }
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      
      if (scrollPercent > maxScroll && scrollPercent % 25 === 0) {
        maxScroll = scrollPercent;
        this.track('Scroll Depth', { percent: scrollPercent });
      }
    });

    // Track time on page (less frequent)
    let timeOnPage = 0;
    setInterval(() => {
      timeOnPage += 30;
      if (timeOnPage % 300 === 0) { // Every 5 minutes instead of 1
        this.track('Time on Page', { seconds: timeOnPage });
      }
    }, 30000); // Check every 30 seconds instead of 10
  }

  // Check if event is critical and should be sent immediately
  isCriticalEvent(eventName) {
    const criticalEvents = [
      'Conversion',
      'Quote Accepted',
      'Payment Completed',
      'Signup Completed',
      'Trial Started',
      'Error'
    ];
    return criticalEvents.includes(eventName);
  }

  // Send events to analytics API
  async flushEvents(synchronous = false) {
    if (!this.isEnabled || this.events.length === 0) return;
    
    const eventsToSend = [...this.events];
    this.events = [];
    
    try {
      const payload = {
        events: eventsToSend,
        sessionId: this.sessionId,
        userId: this.userId
      };
      
      if (synchronous) {
        // Use sendBeacon for synchronous sending (page unload)
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            this.apiEndpoint,
            JSON.stringify(payload)
          );
        }
      } else {
        // Use fetch for async sending with authentication
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json'
        };
        
        // Add authentication header if available
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Analytics events sent successfully:', result);
        }
      }
    } catch (error) {
      console.error('❌ Failed to send analytics events:', error);
      
      // Handle specific error cases
      if (error.message.includes('403') || error.message.includes('upgrade_required')) {
        // User doesn't have pro access, disable analytics to prevent spam
        if (process.env.NODE_ENV === 'development') {
          console.log('📊 Analytics disabled: Pro subscription required');
        }
        this.disable();
        return;
      }
      
      // Re-add events to queue if send failed (but not for auth errors)
      if (!error.message.includes('401') && !error.message.includes('403')) {
        this.events = [...eventsToSend, ...this.events];
        
        // Limit queue size to prevent memory issues
        if (this.events.length > 1000) {
          this.events = this.events.slice(-500); // Keep only latest 500 events
        }
      }
    }
  }

  // Generate unique session ID
  generateSessionId() {
    return 'sess_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  // Get analytics data for admin dashboard
  async getAnalytics(timeframe = '7d') {
    try {
      const response = await fetch(`${this.apiEndpoint}/dashboard?timeframe=${timeframe}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }
      
      return response.json();
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  // Disable analytics (GDPR compliance)
  disable() {
    this.isEnabled = false;
    this.events = [];
  }

  // Enable analytics
  enable() {
    this.isEnabled = true;
    this.init();
  }
}

// Pre-defined tracking helpers for common events
export const trackLandingPageView = () => {
  analytics.trackPageView('Landing Page');
  analytics.trackFunnelStep('Landing Page Visit');
};

export const trackSignupStart = () => {
  analytics.track('Signup Started');
  analytics.trackFunnelStep('Signup Form Viewed');
};

export const trackSignupComplete = (method) => {
  analytics.trackConversion('Signup', { method });
  analytics.trackFunnelStep('Signup Completed');
};

export const trackTrialStart = (plan) => {
  analytics.trackConversion('Trial Started', { plan });
  analytics.trackFunnelStep('Trial Started');
};

export const trackQuoteCreated = (quoteId, tiers) => {
  analytics.trackQuoteEvent('Created', quoteId, { tiers });
  analytics.trackBusinessMetric('Quotes Created', 1);
};

export const trackQuoteViewed = (quoteId, clientType = 'external') => {
  analytics.trackQuoteEvent('Viewed', quoteId, { clientType });
  analytics.trackBusinessMetric('Quote Views', 1);
};

export const trackQuoteAccepted = (quoteId, selectedTier, totalValue) => {
  analytics.trackQuoteEvent('Accepted', quoteId, { 
    selectedTier, 
    totalValue 
  });
  analytics.trackConversion('Quote Accepted', { 
    quoteId, 
    selectedTier, 
    totalValue 
  });
  analytics.trackBusinessMetric('Revenue', totalValue);
};

export const trackDashboardUsage = (action, data = {}) => {
  analytics.track('Dashboard Action', { action, ...data });
};

export const trackProductInteraction = (action, productId, data = {}) => {
  analytics.track('Product Interaction', { 
    action, 
    productId, 
    ...data 
  });
};

// Create singleton instance
export const analytics = new AnalyticsService();

// Auto-track page changes for SPA (less frequent checking)
let currentPath = window.location.pathname;
setInterval(() => {
  if (window.location.pathname !== currentPath) {
    currentPath = window.location.pathname;
    analytics.trackPageView(currentPath);
  }
}, 5000); // Check every 5 seconds instead of 1

export default analytics;